/**
 * Role-Based Access Control (RBAC) Utilities
 * Defines permissions for different team member roles
 */

// Role definitions
export const ROLES = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  MANAGER: 'MANAGER',
  SALES: 'SALES',
  INVENTORY: 'INVENTORY',
  MARKETING: 'MARKETING',
  CASHIER: 'CASHIER',
  VIEWER: 'VIEWER'
};

// Feature access permissions per role
const ROLE_PERMISSIONS = {
  [ROLES.OWNER]: {
    dashboard: { view: true, edit: true },
    products: { view: true, edit: true, create: true, delete: true },
    orders: { view: true, edit: true, create: true, delete: true },
    customers: { view: true, edit: true, create: true, delete: true },
    analytics: { view: true },
    settings: { view: true, edit: true },
    team: { view: true, edit: true, invite: true, remove: true },
    shipping: { view: true, edit: true },
    payments: { view: true, edit: true },
    store: { view: true, edit: true },
    kyc: { view: true, edit: true },
    settlements: { view: true },
    expenses: { view: true, edit: true },
    discounts: { view: true, edit: true },
    campaigns: { view: true, edit: true },
    whatsapp: { view: true, edit: true },
    refunds: { view: true, edit: true },
    media: { view: true, edit: true, create: true, delete: true }
  },

  [ROLES.ADMIN]: {
    dashboard: { view: true, edit: true },
    products: { view: true, edit: true, create: true, delete: true },
    orders: { view: true, edit: true, create: true, delete: true },
    customers: { view: true, edit: true, create: true, delete: true },
    analytics: { view: true },
    settings: { view: true, edit: true },
    team: { view: true, edit: true, invite: true, remove: true },
    shipping: { view: true, edit: true },
    payments: { view: true, edit: true },
    store: { view: true, edit: true },
    kyc: { view: true, edit: false }, // Can't edit KYC
    settlements: { view: true },
    expenses: { view: true, edit: true },
    discounts: { view: true, edit: true },
    campaigns: { view: true, edit: true },
    whatsapp: { view: true, edit: true },
    refunds: { view: true, edit: true },
    media: { view: true, edit: true, create: true, delete: true }
  },

  [ROLES.MANAGER]: {
    dashboard: { view: true, edit: false },
    products: { view: true, edit: true, create: true, delete: true },
    orders: { view: true, edit: true, create: true, delete: true },
    customers: { view: true, edit: true, create: true, delete: true },
    analytics: { view: true },
    settings: { view: true, edit: false }, // View only
    team: { view: true, edit: false, invite: false, remove: false },
    shipping: { view: true, edit: true },
    payments: { view: true, edit: false }, // View only
    store: { view: true, edit: false }, // View only
    kyc: { view: false, edit: false }, // No KYC access
    settlements: { view: true },
    expenses: { view: true, edit: true },
    discounts: { view: true, edit: true },
    campaigns: { view: true, edit: true },
    whatsapp: { view: true, edit: true },
    refunds: { view: true, edit: true },
    media: { view: true, edit: true, create: true, delete: true }
  },

  [ROLES.SALES]: {
    dashboard: { view: true, edit: false },
    products: { view: true, edit: false, create: false, delete: false }, // View only
    orders: { view: true, edit: true, create: true, delete: false }, // Full access
    customers: { view: true, edit: false, create: false, delete: false }, // View only
    analytics: { view: false }, // No analytics
    settings: { view: false, edit: false }, // No settings
    team: { view: false, edit: false, invite: false, remove: false }, // No team management
    shipping: { view: false, edit: false }, // No shipping
    payments: { view: false, edit: false }, // No payments
    store: { view: false, edit: false }, // No store settings
    kyc: { view: false, edit: false }, // No KYC
    settlements: { view: false }, // No settlements
    expenses: { view: false, edit: false }, // No expenses
    discounts: { view: true, edit: false }, // View only
    campaigns: { view: false, edit: false }, // No campaigns
    whatsapp: { view: true, edit: false }, // View only
    refunds: { view: true, edit: false }, // View only
    media: { view: true, edit: false, create: false, delete: false } // View only
  },

  [ROLES.INVENTORY]: {
    dashboard: { view: true, edit: false },
    products: { view: true, edit: true, create: true, delete: false },
    orders: { view: true, edit: true, create: false, delete: false },
    customers: { view: true, edit: false, create: false, delete: false },
    analytics: { view: false },
    settings: { view: false, edit: false },
    team: { view: false, edit: false, invite: false, remove: false },
    shipping: { view: true, edit: false },
    payments: { view: false, edit: false },
    store: { view: false, edit: false },
    kyc: { view: false, edit: false },
    settlements: { view: false },
    expenses: { view: false, edit: false },
    discounts: { view: true, edit: false },
    campaigns: { view: false, edit: false },
    whatsapp: { view: false, edit: false },
    refunds: { view: false, edit: false },
    media: { view: true, edit: true, create: true, delete: false } // Can upload for products
  },

  [ROLES.CASHIER]: {
    dashboard: { view: true, edit: false },
    products: { view: true, edit: false, create: false, delete: false },
    orders: { view: true, edit: true, create: true, delete: false },
    customers: { view: true, edit: false, create: true, delete: false },
    analytics: { view: false },
    settings: { view: false, edit: false },
    team: { view: false, edit: false, invite: false, remove: false },
    shipping: { view: false, edit: false },
    payments: { view: false, edit: false },
    store: { view: false, edit: false },
    kyc: { view: false, edit: false },
    settlements: { view: false },
    expenses: { view: false, edit: false },
    discounts: { view: true, edit: false },
    campaigns: { view: false, edit: false },
    whatsapp: { view: false, edit: false },
    refunds: { view: false, edit: false },
    media: { view: false, edit: false, create: false, delete: false } // No media access
  },

  [ROLES.VIEWER]: {
    dashboard: { view: true, edit: false },
    products: { view: true, edit: false, create: false, delete: false },
    orders: { view: true, edit: false, create: false, delete: false },
    customers: { view: true, edit: false, create: false, delete: false },
    analytics: { view: true },
    settings: { view: false, edit: false },
    team: { view: false, edit: false, invite: false, remove: false },
    shipping: { view: false, edit: false },
    payments: { view: false, edit: false },
    store: { view: false, edit: false },
    kyc: { view: false, edit: false },
    settlements: { view: false },
    expenses: { view: false, edit: false },
    discounts: { view: false, edit: false },
    campaigns: { view: false, edit: false },
    whatsapp: { view: false, edit: false },
    refunds: { view: false, edit: false },
    media: { view: true, edit: false, create: false, delete: false } // View only
  }
};

/**
 * Check if a user has permission for a specific feature and action
 * @param {string} userRole - User's role (OWNER, ADMIN, MANAGER, SALES, etc.)
 * @param {string} feature - Feature to check (products, orders, customers, etc.)
 * @param {string} action - Action to check (view, edit, create, delete)
 * @returns {boolean} - Whether the user has permission
 */
export const hasPermission = (userRole, feature, action = 'view') => {
  // Owners always have full permission
  if (userRole === ROLES.OWNER) {
    return true;
  }

  const rolePermissions = ROLE_PERMISSIONS[userRole];

  if (!rolePermissions) {
    console.warn(`Unknown role: ${userRole}`);
    return false;
  }

  const featurePermissions = rolePermissions[feature];

  if (!featurePermissions) {
    console.warn(`Unknown feature: ${feature}`);
    return false;
  }

  return featurePermissions[action] === true;
};

/**
 * Check if a user can view a feature
 * @param {string} userRole - User's role
 * @param {string} feature - Feature to check
 * @returns {boolean}
 */
export const canView = (userRole, feature) => {
  return hasPermission(userRole, feature, 'view');
};

/**
 * Check if a user can edit a feature
 * @param {string} userRole - User's role
 * @param {string} feature - Feature to check
 * @returns {boolean}
 */
export const canEdit = (userRole, feature) => {
  return hasPermission(userRole, feature, 'edit');
};

/**
 * Check if a user can create in a feature
 * @param {string} userRole - User's role
 * @param {string} feature - Feature to check
 * @returns {boolean}
 */
export const canCreate = (userRole, feature) => {
  return hasPermission(userRole, feature, 'create');
};

/**
 * Check if a user can delete in a feature
 * @param {string} userRole - User's role
 * @param {string} feature - Feature to check
 * @returns {boolean}
 */
export const canDelete = (userRole, feature) => {
  return hasPermission(userRole, feature, 'delete');
};

/**
 * Get all permissions for a specific role
 * @param {string} userRole - User's role
 * @returns {object} - All permissions for the role
 */
export const getRolePermissions = (userRole) => {
  return ROLE_PERMISSIONS[userRole] || {};
};

/**
 * Check if user is store owner
 * @param {string} userRole - User's role
 * @returns {boolean}
 */
export const isOwner = (userRole) => {
  return userRole === ROLES.OWNER;
};

/**
 * Check if user is admin or owner
 * @param {string} userRole - User's role
 * @returns {boolean}
 */
export const isAdminOrOwner = (userRole) => {
  return userRole === ROLES.OWNER || userRole === ROLES.ADMIN;
};

/**
 * Check if user can manage team (invite/remove members)
 * @param {string} userRole - User's role
 * @returns {boolean}
 */
export const canManageTeam = (userRole) => {
  return hasPermission(userRole, 'team', 'invite');
};

/**
 * Get user-friendly role name
 * @param {string} role - Role code
 * @returns {string} - User-friendly role name
 */
export const getRoleName = (role) => {
  const roleNames = {
    [ROLES.OWNER]: 'Owner',
    [ROLES.ADMIN]: 'Administrator',
    [ROLES.MANAGER]: 'Manager',
    [ROLES.SALES]: 'Sales',
    [ROLES.INVENTORY]: 'Inventory',
    [ROLES.MARKETING]: 'Marketing',
    [ROLES.CASHIER]: 'Cashier',
    [ROLES.VIEWER]: 'Viewer'
  };

  return roleNames[role] || role;
};
